import { z } from 'zod';

import { AiProvider } from './ai';
import { defaultNullish, mongoIdSchema } from './shared';

export enum StripeCouponDuration {
  Once = 'once',
  Recurring = 'recurring',
  Forever = 'forever',
}

export enum PlanKind {
  Legacy = 'legacy',
  Custom = 'custom',
  Lite = 'lite',
  Grow = 'grow',
  Scale = 'scale',
  Enterprise = 'enterprise',
}

export enum Currency {
  Eur = 'eur',
  Usd = 'usd',
}

export enum SubscriptionPaymentCycle {
  Month = 'month',
  Year = 'year',
}

export enum CancelReason {
  CustomerService = 'customer_service',
  LowQuality = 'low_quality',
  MissingFeatures = 'missing_features',
  SwitchedService = 'switched_service',
  TooComplex = 'too_complex',
  TooExpensive = 'too_expensive',
  Unused = 'unused',
  Other = 'other',
}

export enum PlanStatus {
  NotSubscribed = 'notsubscribed', // User has no subscription record yet (fresh sign-up).
  Trialing = 'trialing', // User started a subscription with a trial period.
  Subscribed = 'subscribed', // User has an active paid subscription after trial.
  Switched = 'switched', // User switched to a different plan during a subscription.
  PastDue = 'pastdue', // Payment attempt failed. User is in grace period while on retries.
  Cancelling = 'cancelling', // User has requested to cancel at the end of the billing period.
  Cancelled = 'cancelled', // Subscription has ended.
}

export enum PlanCancelFeedback {
  CustomerService = 'customer_service',
  LowQuality = 'low_quality',
  MissingFeatures = 'missing_features',
  Other = 'other',
  SwitchedService = 'switched_service',
  TooComplex = 'too_complex',
  TooExpensive = 'too_expensive',
  Unused = 'unused',
}
export const planSchema = z.object({
  kind: z.nativeEnum(PlanKind),
  currency: z.nativeEnum(Currency),
  paymentCycle: z.nativeEnum(SubscriptionPaymentCycle),

  pricePerMonth: z.number(),
  priceDiscount: z.number(),
  baseCreditsPerMonth: z.number(),
  extraCreditPrice: z.number(),
  extraCreditDiscount: z.number(),

  providerPlanId: z.string(),
  providerPriceId: z.string(),
  providerExtraCreditPriceId: z.string(),

  trialDays: z.number(),
  trialArticles: z.number(),
  trialProducts: z.number(),

  // features
  aiOverview: z.array(z.nativeEnum(AiProvider)),
  hasAiContentGap: z.boolean(),
  hasAiMetrics: z.boolean(),
  hasRefreshContentLight: z.boolean(),
  hasRefreshContentMajor: z.boolean(),
  maxTeamUserCount: z.number(),
  maxBrandCount: z.number(),
  hasSupportingMaterials: z.boolean(),

  enabledArticles: z.boolean(),
  enabledProducts: z.boolean(),
  enabledCollections: z.boolean(),

  planId: mongoIdSchema,
  planCreditCount: z.number(),
  extraCreditCount: z.number(),

  pendingDowngrade: z
    .object({
      planId: mongoIdSchema,
      providerPlanId: z.string(),
      paymentCycle: z.nativeEnum(SubscriptionPaymentCycle),
      effectiveAt: z.number(),
    })
    .nullish(),
});
export type Plan = z.infer<typeof planSchema>;

export const planEnum = z.nativeEnum(PlanKind);
export const planRecurringEnum = z.nativeEnum(SubscriptionPaymentCycle);

export const organizationBillingCreateSchema = z.object({
  organizationId: mongoIdSchema,

  providerCustomerId: z.string().nullish(), // Stripe customer ID
  providerSubscriptionId: z.string().nullish(), // Stripe subscription ID

  currentPlanStatus: defaultNullish(z.nativeEnum(PlanStatus).default(PlanStatus.NotSubscribed)),
  currentPlanEndDate: defaultNullish(z.number().default(0)),

  cancelReason: z.nativeEnum(CancelReason).nullish(),
  cancelExtraDetails: z.string().nullish(),

  // Track Legacy users
  isLegacy: z.boolean().nullish(),
  // Tolt referral ID
  toltReferralId: z.string().nullish(),

  // new payment system (v3)
  plan: defaultNullish(planSchema.nullish()),

  // LEGACY: v2
  currentOriginalMonthlyPriceAmount: defaultNullish(z.number().default(0)),
  currentMonthlyPriceAmount: defaultNullish(z.number().default(0)),
  currentPlanRecurring: defaultNullish(
    z.nativeEnum(SubscriptionPaymentCycle).default(SubscriptionPaymentCycle.Month)
  ),
  currentCurrency: defaultNullish(z.nativeEnum(Currency).default(Currency.Eur)),
  currentPlanId: z.string().nullish(),
  currentPriceId: z.string().nullish(),
  currentPlanName: z.string().nullish(),
  aiSubCredits: defaultNullish(z.number().default(0)),
  // used to understand if the organization has access to certain features
  features: z.object({
    monthlyProductCredits: z.number(),
    yearlyProductCredits: z.number(),
    enabledArticles: z.boolean(),
    enabledProducts: z.boolean(),
    enabledCollections: z.boolean(),
  }),

  // LEGACY: data from the old subscription structure
  discountCodes: defaultNullish(z.array(z.string()).default([])),
  currentPlan: z.string().nullish(),
  currentPriceAmount: defaultNullish(z.number().default(0)),
  currentPlanRegisterDate: defaultNullish(z.number().default(0)),
  currentPlanStartDate: defaultNullish(z.number().default(0)),
  nextPlanBillingDate: defaultNullish(z.number().default(0)),
});

export type OrganizationBillingCreate = z.infer<typeof organizationBillingCreateSchema>;

export const organizationBillingSchema = z
  .object({
    _id: mongoIdSchema,
    hasFreeArticleCreated: z.boolean().nullish(),
    hasFreeAuditCreated: z.boolean().nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .merge(organizationBillingCreateSchema);

export type OrganizationBilling = z.infer<typeof organizationBillingSchema>;

// Error Typings
export const UNABLE_TO_ARCHIVE_OWNER_WITH_ACTIVE_SUBSCRIPTION =
  'UNABLE_TO_ARCHIVE_OWNER_WITH_ACTIVE_SUBSCRIPTION';
export const UNABLE_TO_ARCHIVE_OWNER_WITH_MULTIPLE_USERS =
  'UNABLE_TO_ARCHIVE_OWNER_WITH_MULTIPLE_USERS';
